import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, ImageIcon, PackageCheck, MessageSquarePlus } from "lucide-react";
import { api, formatApiError, fileUrl } from "@/lib/api";

const fmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 });

const STATUS = {
  awaiting_payment: "border-amber-400 text-amber-700",
  payment_reported: "border-sky-500/50 text-sky-700",
  pending_confirmation: "border-[#6D28D9]/50 text-[#6D28D9]",
  confirmed: "border-emerald-600/40 text-emerald-700",
  allotted: "border-emerald-700 bg-emerald-600 text-white",
  created: "border-slate-300 text-slate-500",
};

export const OrdersTab = () => {
  const [orders, setOrders] = useState([]);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const confirm = async (id) => {
    setBusy(id);
    try {
      await api.patch(`/admin/orders/${id}/confirm`);
      toast.success("Order confirmed.");
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const allot = async (id) => {
    const text = window.prompt("Optional remark for the investor (e.g. transferred to demat on 23 Sep):", "");
    if (text === null) return;
    setBusy(id);
    try {
      await api.patch(`/admin/orders/${id}/allot`, { step: "allotted", text: text.trim() || "Shares transferred to your demat account." });
      toast.success("Marked as allotted — investor notified.");
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const remark = async (o) => {
    const text = window.prompt(`Desk remark for ${o.reference || o.company_name} (visible to the investor):`, "");
    if (!text || !text.trim()) return;
    const step = o.status === "allotted" ? "allotted" : o.status === "confirmed" ? "payment_verified" : o.utr || o.payment_status === "paid" ? "payment_reported" : "placed";
    setBusy(o.id);
    try {
      await api.post(`/admin/orders/${o.id}/remarks`, { step, text: text.trim() });
      toast.success("Remark added — investor notified.");
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const paid = orders.filter((o) => o.payment_status === "paid" || (o.provider === "bank_transfer" && o.status !== "abandoned"));
  const total = paid.filter((o) => o.payment_status === "paid").reduce((s, o) => s + (o.amount || 0), 0);
  const pending = paid.filter((o) => ["pending_confirmation", "payment_reported"].includes(o.status)).length;

  return (
    <div data-testid="admin-orders-tab">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight">Share Orders</h2>
          <p className="mt-1 text-sm text-slate-500">{paid.length} paid · ₹{fmt.format(total)} collected · {pending} awaiting confirmation.</p>
        </div>
      </div>
      <div className="mt-8 overflow-x-auto border border-slate-200">
        <table className="w-full min-w-[820px] text-left text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-[#F6F4FD] font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500">
              <th className="px-5 py-4">Placed</th>
              <th className="px-5 py-4">Investor</th>
              <th className="px-5 py-4">Share</th>
              <th className="px-5 py-4">Qty</th>
              <th className="px-5 py-4">Value</th>
              <th className="px-5 py-4">Status</th>
              <th className="px-5 py-4">Action</th>
            </tr>
          </thead>
          <tbody>
            {paid.map((o) => (
              <tr key={o.id} data-testid={`admin-order-row-${o.id}`} className="border-b border-slate-100 hover:bg-violet-50">
                <td className="px-5 py-3 font-mono2 text-xs text-slate-500">{new Date(o.created_at).toLocaleDateString("en-IN")}</td>
                <td className="px-5 py-3 text-slate-700">{o.name}<br /><span className="font-mono2 text-[10px] text-slate-500">{o.phone || o.email}</span></td>
                <td className="px-5 py-3 text-slate-800">{o.company_name}{o.provider === "bank_transfer" && <><br /><span data-testid={`order-bank-ref-${o.id}`} className="font-mono2 text-[10px] text-[#6D28D9]">Bank · {o.reference}{o.utr ? ` · UTR ${o.utr}` : ""}</span></>}
                  {o.proof_path && <><br /><a data-testid={`order-proof-link-${o.id}`} href={fileUrl(o.proof_path)} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 border border-sky-500/40 px-2 py-0.5 font-mono2 text-[9px] uppercase tracking-[0.15em] text-sky-700 hover:bg-sky-50"><ImageIcon className="h-3 w-3" /> View screenshot</a></>}</td>
                <td className="px-5 py-3 font-mono2 text-slate-700">{o.quantity}</td>
                <td className="px-5 py-3 font-mono2 text-slate-800">₹{fmt.format(o.amount)}</td>
                <td className="px-5 py-3"><span className={`border px-2.5 py-1 font-mono2 text-[9px] uppercase tracking-[0.15em] ${STATUS[o.status] || STATUS.created}`}>{(o.status || "").replace(/_/g, " ")}</span>{o.remarks?.length > 0 && <span data-testid={`order-remarks-count-${o.id}`} className="ml-2 font-mono2 text-[9px] text-slate-400">{o.remarks.length} remark{o.remarks.length > 1 ? "s" : ""}</span>}</td>
                <td className="px-5 py-3">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {["pending_confirmation", "payment_reported"].includes(o.status) && (
                      <button data-testid={`confirm-order-${o.id}`} onClick={() => confirm(o.id)} disabled={busy === o.id} className="flex items-center gap-1.5 border border-emerald-600/40 px-3 py-1.5 font-mono2 text-[9px] uppercase tracking-[0.15em] text-emerald-700 hover:bg-emerald-600/10 disabled:opacity-40">
                        {busy === o.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />} {o.status === "payment_reported" ? "Verify & confirm" : "Confirm"}
                      </button>
                    )}
                    {o.status === "confirmed" && (
                      <button data-testid={`allot-order-${o.id}`} onClick={() => allot(o.id)} disabled={busy === o.id} className="flex items-center gap-1.5 bg-emerald-600 px-3 py-1.5 font-mono2 text-[9px] font-semibold uppercase tracking-[0.15em] text-white hover:bg-emerald-700 disabled:opacity-40">
                        {busy === o.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <PackageCheck className="h-3 w-3" />} Mark allotted
                      </button>
                    )}
                    {o.status !== "awaiting_payment" && (
                      <button data-testid={`remark-order-${o.id}`} onClick={() => remark(o)} disabled={busy === o.id} className="flex items-center gap-1.5 border border-slate-200 px-3 py-1.5 font-mono2 text-[9px] uppercase tracking-[0.15em] text-slate-600 hover:border-[#6D28D9]/50 hover:text-[#6D28D9] disabled:opacity-40" aria-label="Add remark">
                        <MessageSquarePlus className="h-3 w-3" /> Remark
                      </button>
                    )}
                    {o.status === "awaiting_payment" && <span className="font-mono2 text-[9px] uppercase tracking-[0.15em] text-slate-400">—</span>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {paid.length === 0 && <p data-testid="admin-no-orders" className="px-5 py-12 text-center font-mono2 text-xs uppercase tracking-[0.25em] text-slate-500">No share orders yet</p>}
      </div>
    </div>
  );
};
